<template>
  <footer
    class="footer"
    :class="{ 'footer--attached-to-content': isAttachedToContent }">
    <div class="container">
      <div class="footer__main">
        <div class="footer__brand">
          <NuxtLink
            :to="localePath('/')"
            class="footer__logo"
            >PrePeers</NuxtLink
          >
          <p class="footer__tagline">L'orientation 100% gratuite,<br />qui part de toi.</p>
          <div class="footer__stats">
            <div class="footer__stat">
              <span class="footer__stat-value">+50 000</span>
              <span class="footer__stat-label">étudiants</span>
            </div>
            <div class="footer__stat">
              <span class="footer__stat-value">25 000+</span>
              <span class="footer__stat-label">programmes</span>
            </div>
            <div class="footer__stat">
              <span class="footer__stat-value">4 000+</span>
              <span class="footer__stat-label">métiers</span>
            </div>
          </div>
          <a
            :href="`mailto:${CONTACT_EMAIL}`"
            class="footer__link footer__email"
            >{{ CONTACT_EMAIL }}</a
          >
        </div>

        <nav class="footer__nav">
          <div
            v-for="col in footerLinks"
            :key="col.title"
            class="footer__col">
            <p class="footer__col-title">{{ col.title }}</p>
            <ul class="footer__col-list">
              <li
                v-for="link in col.links"
                :key="link.to">
                <NuxtLink
                  :to="link.to"
                  :external="isExternalLink(link.to)"
                  :target="isExternalLink(link.to) ? '_blank' : undefined"
                  :rel="isExternalLink(link.to) ? 'noopener noreferrer' : undefined"
                  class="footer__link"
                  >{{ link.label }}</NuxtLink
                >
              </li>
            </ul>
          </div>
        </nav>
      </div>

      <div class="divider-glow"></div>

      <div class="footer__bottom">
        <p class="footer__copyright">© {{ currentYear }} PrePeers — Tous droits réservés</p>
        <div class="footer__legal">
          <NuxtLink
            :to="localePath('/notre-mission')"
            class="footer__legal-link"
            >Notre mission</NuxtLink
          >
          <NuxtLink
            :to="localePath('/conditions-generales-utilisation')"
            class="footer__legal-link"
            >Conditions d'utilisation</NuxtLink
          >
          <NuxtLink
            :to="localePath('/politique-de-confidentialite')"
            class="footer__legal-link"
            >Politique de confidentialité</NuxtLink
          >
          <NuxtLink
            :to="`${localePath('/comment-lola-decide')}#parametres-de-classement`"
            class="footer__legal-link"
            >{{ $t('commentLolaDecide.rankingParameters.title') }}</NuxtLink
          >
          <NuxtLink
            :to="localePath('/mentions-legales')"
            class="footer__legal-link"
            >Mentions légales</NuxtLink
          >
          <NuxtLink
            to="https://edu.prepeers.co/"
            external
            target="_blank"
            rel="noopener noreferrer"
            class="footer__legal-link"
            >Espace pro</NuxtLink
          >
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'

const CONTACT_EMAIL: string = 'info@prepeers.co'

const localePath = useLocalePath()
const route: ReturnType<typeof useRoute> = useRoute()
const currentYear = computed(() => new Date().getFullYear())

const isAttachedToContent: ComputedRef<boolean> = computed(
  (): boolean => route.meta.isFooterAttachedToContent === true,
)

function isExternalLink(to: string): boolean {
  return to.startsWith('http://') || to.startsWith('https://')
}

const footerLinks = computed(() => [
  {
    title: 'Orientation',
    links: [{ label: 'Discuter avec Lola', to: localePath('/register') }],
  },
  {
    title: 'Explorer',
    links: [
      { label: 'Les écoles', to: localePath('/recherche/etablissement') },
      { label: "Type d'écoles", to: localePath('/types-ecoles') },
      { label: 'Les métiers', to: localePath('/metiers') },
      { label: 'Les diplômes', to: localePath('/diplomes') },
      { label: 'Où étudier', to: localePath('/ou-etudier') },
      { label: 'Nos conseils', to: localePath('/nos-conseils') },
    ],
  },
  {
    title: 'PrePeers',
    links: [
      { label: 'Notre mission', to: localePath('/notre-mission') },
      { label: 'Comment Lola décide', to: localePath('/comment-lola-decide') },
      { label: 'Espace pro', to: 'https://edu.prepeers.co/' },
      { label: 'Se connecter', to: localePath('/login') },
    ],
  },
])
</script>

<style scoped>
.footer {
  background: var(--bg-surface);
  border-top: 1px solid var(--border-subtle);
  padding-block: var(--space-16) var(--space-8);
  margin-top: var(--space-24);
}

.footer--attached-to-content {
  margin-top: 0;
}

.footer__main {
  display: flex;
  gap: var(--space-16);
  margin-bottom: var(--space-12);
}

.footer__logo {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: var(--text-xl);
  color: var(--text-primary);
  letter-spacing: var(--tracking-tight);
  text-decoration: none;
  display: block;
  margin-bottom: var(--space-3);
}

.footer__tagline {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-6);
}

.footer__stats {
  display: flex;
  gap: var(--space-6);
}

.footer__stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.footer__stat-value {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: var(--text-base);
  background: var(--gradient-match);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.footer__stat-label {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.footer__nav {
  display: flex;
  gap: var(--space-12);
  flex: 1;
  justify-content: flex-end;
}

.footer__col-title {
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: var(--space-4);
}

.footer__col-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.footer__link {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.footer__link:hover {
  color: var(--text-primary);
}

.footer__email {
  display: inline-block;
  margin-top: var(--space-5);
}

.footer__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: var(--space-6);
  flex-wrap: wrap;
  gap: var(--space-4);
}

.footer__copyright {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.footer__legal {
  display: flex;
  gap: var(--space-6);
  flex-wrap: wrap;
}

.footer__legal-link {
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.footer__legal-link:hover {
  color: var(--text-secondary);
}

@media (max-width: 768px) {
  .footer__main {
    flex-direction: column;
    gap: var(--space-10);
  }
  .footer__nav {
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: var(--space-8);
  }
  .footer__bottom {
    flex-direction: column;
    align-items: flex-start;
  }
  .footer__legal {
    gap: var(--space-4);
  }
}
</style>
